import { Component, inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationService, Confirmation } from '@abp/ng.theme.shared';
import { LocalizationModule } from '@abp/ng.core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-custom-confirmation',
  imports: [CommonModule, LocalizationModule],
  template: `
    <!-- Overlay -->
    <div
      class="sudo-confirm-overlay"
      *ngIf="visible"
      [class.fade-in]="visible"
      (click)="onOverlayClick()"
    >
      <!-- Dialog Card -->
      <div
        class="sudo-confirm-card"
        [class.slide-in]="visible"
        (click)="$event.stopPropagation()"
        role="alertdialog"
        [attr.aria-label]="data?.title"
      >
        <!-- Icon -->
        <div class="sudo-confirm-icon" [ngClass]="'icon-' + severity">
          <i [ngClass]="iconClass"></i>
        </div>

        <!-- Title -->
        <h3 class="sudo-confirm-title" *ngIf="data?.title">
          {{ data!.title | abpLocalization }}
        </h3>

        <!-- Message -->
        <p class="sudo-confirm-message" *ngIf="data?.message">
          {{ data!.message | abpLocalization }}
        </p>

        <!-- Actions -->
        <div class="sudo-confirm-actions" [class.single]="isSingleButton">
          <button
            *ngIf="!data?.options?.hideCancelBtn"
            class="sudo-confirm-btn btn-cancel"
            (click)="reject()"
          >
            {{ cancelText | abpLocalization }}
          </button>
          <button
            *ngIf="!data?.options?.hideYesBtn"
            class="sudo-confirm-btn"
            [ngClass]="'btn-' + severity"
            (click)="confirm()"
          >
            {{ yesText | abpLocalization }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .sudo-confirm-overlay {
      position: fixed;
      inset: 0;
      z-index: 10500;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      opacity: 0;
      animation: fadeIn 0.2s ease forwards;
    }

    @keyframes fadeIn {
      to { opacity: 1; }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: scale(0.9) translateY(10px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    .sudo-confirm-card {
      background: #fff;
      border-radius: 20px;
      padding: 32px;
      max-width: 400px;
      width: 90%;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      animation: slideIn 0.25s ease forwards;
    }

    /* Icon */
    .sudo-confirm-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;

      i {
        font-size: 28px;
        color: #fff;
      }
    }

    .icon-error {
      background: linear-gradient(135deg, #F87171 0%, #EF4444 100%);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    }

    .icon-warning {
      background: linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }

    .icon-success {
      background: linear-gradient(135deg, #34D399 0%, #10B981 100%);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    .icon-info {
      background: linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .icon-neutral {
      background: linear-gradient(135deg, #7C7EF5 0%, #6366F1 100%);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }

    /* Title */
    .sudo-confirm-title {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1F2937;
      margin: 0 0 8px;
    }

    /* Message */
    .sudo-confirm-message {
      font-size: 0.9375rem;
      color: #6B7280;
      line-height: 1.6;
      margin: 0 0 28px;
      word-break: break-word;
    }

    /* Actions */
    .sudo-confirm-actions {
      display: flex;
      gap: 12px;
      justify-content: center;

      &.single {
        justify-content: center;
      }
    }

    .sudo-confirm-btn {
      padding: 10px 28px;
      border-radius: 12px;
      font-size: 0.875rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;
      border: none;
      min-width: 100px;
    }

    .btn-cancel {
      background: #F3F4F6;
      color: #4B5563;

      &:hover {
        background: #E5E7EB;
      }
    }

    .btn-error {
      background: linear-gradient(135deg, #F87171 0%, #EF4444 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25);

      &:hover {
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
        transform: translateY(-1px);
      }
    }

    .btn-warning {
      background: linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);

      &:hover {
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
        transform: translateY(-1px);
      }
    }

    .btn-success {
      background: linear-gradient(135deg, #34D399 0%, #10B981 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);

      &:hover {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
        transform: translateY(-1px);
      }
    }

    .btn-info {
      background: linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);

      &:hover {
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
        transform: translateY(-1px);
      }
    }

    .btn-neutral {
      background: linear-gradient(135deg, #7C7EF5 0%, #6366F1 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);

      &:hover {
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);
        transform: translateY(-1px);
      }
    }
  `],
})
export class CustomConfirmationComponent implements OnInit, OnDestroy {
  private confirmationService = inject(ConfirmationService);
  private destroy$ = new Subject<void>();

  data: Confirmation.DialogData | null = null;
  visible = false;

  private static readonly ICON_MAP: Record<string, string> = {
    error: 'fas fa-times',
    warning: 'fas fa-exclamation-triangle',
    success: 'fas fa-check',
    info: 'fas fa-info',
    neutral: 'fas fa-question',
  };

  get severity(): string {
    return this.data?.severity || 'neutral';
  }

  get iconClass(): string {
    return CustomConfirmationComponent.ICON_MAP[this.severity] || 'fas fa-question';
  }

  get yesText(): string {
    return (this.data?.options?.yesText as string) || 'AbpUi::Yes';
  }

  get cancelText(): string {
    return (this.data?.options?.cancelText as string) || 'AbpUi::Cancel';
  }

  get isSingleButton(): boolean {
    return !!(this.data?.options?.hideCancelBtn || this.data?.options?.hideYesBtn);
  }

  ngOnInit(): void {
    this.confirmationService.confirmation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        // Skip empty/error-only data (errors are handled by ToastErrorHandlerService)
        if (!data || (!data.message && !data.title)) {
          return;
        }
        this.data = data;
        this.visible = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirm(): void {
    this.visible = false;
    this.confirmationService.clear(Confirmation.Status.confirm);
  }

  reject(): void {
    this.visible = false;
    this.confirmationService.clear(Confirmation.Status.reject);
  }

  dismiss(): void {
    this.visible = false;
    this.confirmationService.clear(Confirmation.Status.dismiss);
  }

  onOverlayClick(): void {
    if (this.data?.options?.dismissible !== false) {
      this.dismiss();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible && this.data?.options?.dismissible !== false) {
      this.dismiss();
    }
  }
}
